<script setup lang="ts">
// The district's application equipment: the vocabulary every treatment-filing
// picker reads. It is district data rather than a VectorSurv credential — a rig
// is retired, never deleted, because the captures that name it stay readable.
// The list mirrors the district's own VectorSurv account: its equipment kinds
// are imported here as rigs, and only a rig with no upstream counterpart is
// created upstream, the first time a treatment needs it.
import { onMounted, reactive, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import type {
	VectorSurvEquipment,
	VectorSurvEquipmentRequest,
} from "@/type/vectorsurv";

interface EquipmentForm {
	name: string;
	manufacturer: string;
	model: string;
	serial: string;
}

const equipment = ref<VectorSurvEquipment[]>([]);
const equipmentLoading = ref(false);
const equipmentSaving = ref(false);
const equipmentError = ref("");
const editingEquipmentID = ref<number | null>(null);
const newEquipment = reactive<EquipmentForm>({
	name: "",
	manufacturer: "",
	model: "",
	serial: "",
});
const editEquipment = reactive<EquipmentForm>({
	name: "",
	manufacturer: "",
	model: "",
	serial: "",
});

/** normalise turns a form field into the wire value: blank is absent, not an
 * empty string. */
function normaliseField(value: string): string | null {
	const trimmed = value.trim();
	return trimmed === "" ? null : trimmed;
}

function equipmentRequest(form: EquipmentForm): VectorSurvEquipmentRequest {
	return {
		name: form.name.trim(),
		manufacturer: normaliseField(form.manufacturer),
		model: normaliseField(form.model),
		serial: normaliseField(form.serial),
	};
}

function clearEquipmentForm(form: EquipmentForm): void {
	form.name = "";
	form.manufacturer = "";
	form.model = "";
	form.serial = "";
}

async function loadEquipment(): Promise<void> {
	equipmentLoading.value = true;
	equipmentError.value = "";
	try {
		equipment.value = await apiClient.JSONGet<VectorSurvEquipment[]>(
			"/api/integration/vectorsurv/equipment",
		);
	} catch (e: any) {
		equipmentError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load the equipment list.";
	} finally {
		equipmentLoading.value = false;
	}
}

function startEquipmentEdit(rig: VectorSurvEquipment): void {
	editingEquipmentID.value = rig.id;
	editEquipment.name = rig.name;
	editEquipment.manufacturer = rig.manufacturer ?? "";
	editEquipment.model = rig.model ?? "";
	editEquipment.serial = rig.serial ?? "";
}

function cancelEquipmentEdit(): void {
	editingEquipmentID.value = null;
	clearEquipmentForm(editEquipment);
}

async function addEquipment(): Promise<void> {
	if (equipmentSaving.value || !newEquipment.name.trim()) return;
	equipmentSaving.value = true;
	equipmentError.value = "";
	try {
		await apiClient.JSONPost<VectorSurvEquipment>(
			"/api/integration/vectorsurv/equipment",
			equipmentRequest(newEquipment),
		);
		clearEquipmentForm(newEquipment);
		await loadEquipment();
	} catch (e: any) {
		equipmentError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to add the equipment.";
	} finally {
		equipmentSaving.value = false;
	}
}

async function saveEquipmentEdit(): Promise<void> {
	const id = editingEquipmentID.value;
	if (id === null || equipmentSaving.value || !editEquipment.name.trim())
		return;
	equipmentSaving.value = true;
	equipmentError.value = "";
	try {
		await apiClient.JSONPut<VectorSurvEquipment>(
			`/api/integration/vectorsurv/equipment/${id}`,
			equipmentRequest(editEquipment),
		);
		cancelEquipmentEdit();
		await loadEquipment();
	} catch (e: any) {
		equipmentError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to save the equipment.";
	} finally {
		equipmentSaving.value = false;
	}
}

async function retireEquipment(rig: VectorSurvEquipment): Promise<void> {
	if (
		!confirm(
			`Retire “${rig.name}”? Treatments already captured with it keep naming it; it is no longer offered for new captures.`,
		)
	) {
		return;
	}
	equipmentSaving.value = true;
	equipmentError.value = "";
	try {
		await apiClient.JSONPost<VectorSurvEquipment>(
			`/api/integration/vectorsurv/equipment/${rig.id}/retire`,
			{},
		);
		await loadEquipment();
	} catch (e: any) {
		equipmentError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to retire the equipment.";
	} finally {
		equipmentSaving.value = false;
	}
}

onMounted(loadEquipment);
</script>

<template>
	<div class="container py-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div class="d-flex align-items-center">
				<RouterLink
					class="btn btn-outline-secondary me-3"
					to="/_/configuration/integration/vectorsurv"
					title="Back to VectorSurv"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				<div>
					<h1 class="mb-0">VectorSurv equipment</h1>
					<p class="text-muted mb-0">
						The rigs a treatment can be captured as applied with
					</p>
				</div>
			</div>
		</div>

		<div class="card mb-4 integration-card">
			<div class="card-header">
				<div>
					<h2 class="h5 mb-0">Equipment</h2>
				</div>
			</div>
			<div class="card-body">
				<p class="text-muted">
					The district's application equipment, mirrored from the district's own
					VectorSurv account: its equipment kinds arrive here as rigs, and a
					rename or a retirement there follows. A treatment files in VectorSurv
					only when its equipment and applicator are captured against it, so
					this list is what the treatment-filing pickers offer. A rig the
					district adds here that VectorSurv does not have is created upstream
					the first time a treatment needs it; retiring a rig keeps every filing
					that already names it.
				</p>

				<div v-if="equipmentError" class="alert alert-danger" role="alert">
					{{ equipmentError }}
				</div>

				<div v-if="equipmentLoading" class="text-muted">Loading equipment…</div>

				<div v-else-if="equipment.length === 0" class="text-muted">
					No equipment yet. Add the district's rigs below.
				</div>

				<table v-else class="table table-sm align-middle mb-0">
					<thead>
						<tr>
							<th scope="col">Name</th>
							<th scope="col">Manufacturer</th>
							<th scope="col">Model</th>
							<th scope="col">Serial</th>
							<th scope="col">Status</th>
							<th scope="col" class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="rig in equipment"
							:key="rig.id"
							:class="{ 'text-muted': !rig.active }"
						>
							<template v-if="editingEquipmentID === rig.id">
								<td>
									<input
										v-model="editEquipment.name"
										class="form-control form-control-sm"
									/>
								</td>
								<td>
									<input
										v-model="editEquipment.manufacturer"
										class="form-control form-control-sm"
									/>
								</td>
								<td>
									<input
										v-model="editEquipment.model"
										class="form-control form-control-sm"
									/>
								</td>
								<td>
									<input
										v-model="editEquipment.serial"
										class="form-control form-control-sm"
									/>
								</td>
								<td>
									<span class="badge text-bg-secondary">editing</span>
								</td>
								<td class="text-end text-nowrap">
									<button
										type="button"
										class="btn btn-sm btn-primary"
										:disabled="!editEquipment.name.trim()"
										@click="saveEquipmentEdit"
									>
										Save
									</button>
									<button
										type="button"
										class="btn btn-sm btn-outline-secondary ms-1"
										@click="cancelEquipmentEdit"
									>
										Cancel
									</button>
								</td>
							</template>
							<template v-else>
								<td>{{ rig.name }}</td>
								<td>{{ rig.manufacturer || "—" }}</td>
								<td>{{ rig.model || "—" }}</td>
								<td>{{ rig.serial || "—" }}</td>
								<td>
									<span
										class="badge"
										:class="
											rig.active ? 'text-bg-success' : 'text-bg-secondary'
										"
									>
										{{ rig.active ? "in use" : "retired" }}
									</span>
								</td>
								<td class="text-end text-nowrap">
									<button
										type="button"
										class="btn btn-sm btn-outline-secondary"
										@click="startEquipmentEdit(rig)"
									>
										Edit
									</button>
									<button
										v-if="rig.active"
										type="button"
										class="btn btn-sm btn-outline-danger ms-1"
										@click="retireEquipment(rig)"
									>
										Retire
									</button>
								</td>
							</template>
						</tr>
					</tbody>
				</table>

				<form
					class="row g-2 align-items-end mt-3"
					@submit.prevent="addEquipment"
				>
					<div class="col-md-3">
						<label class="form-label" for="equipment-name">Name</label>
						<input
							id="equipment-name"
							v-model="newEquipment.name"
							class="form-control form-control-sm"
							placeholder="Truck 12"
							required
						/>
					</div>
					<div class="col-md-2">
						<label class="form-label" for="equipment-manufacturer"
							>Manufacturer</label
						>
						<input
							id="equipment-manufacturer"
							v-model="newEquipment.manufacturer"
							class="form-control form-control-sm"
						/>
					</div>
					<div class="col-md-2">
						<label class="form-label" for="equipment-model">Model</label>
						<input
							id="equipment-model"
							v-model="newEquipment.model"
							class="form-control form-control-sm"
						/>
					</div>
					<div class="col-md-2">
						<label class="form-label" for="equipment-serial">Serial</label>
						<input
							id="equipment-serial"
							v-model="newEquipment.serial"
							class="form-control form-control-sm"
						/>
					</div>
					<div class="col-md-3">
						<ButtonLoading
							text="Add equipment"
							icon="bi bi-plus-lg"
							:loading="equipmentSaving"
							:disabled="!newEquipment.name.trim()"
							@click="addEquipment"
						/>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<style scoped>
.integration-card {
	border-left: 5px solid #0d6efd;
}
</style>
